import * as React from 'react'
import type { CSSProperties } from 'react'

import { cn } from '@/lib/utils'

/**
 * Card primitive per design spec: subtle shadow, rounded corners.
 */
export const Card = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, style, ...props }, ref) => (
    <div
      ref={ref}
      style={{ ...styles.card, ...style }}
      className={cn('card', className)}
      {...props}
    />
  )
)
Card.displayName = 'Card'

export const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, style, ...props }, ref) => (
    <div
      ref={ref}
      style={{ ...styles.header, ...style }}
      className={className}
      {...props}
    />
  )
)
CardHeader.displayName = 'CardHeader'

export const CardTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, style, ...props }, ref) => (
    <h3
      ref={ref}
      style={{ ...styles.title, ...style }}
      className={className}
      {...props}
    />
  )
)
CardTitle.displayName = 'CardTitle'

export const CardDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, style, ...props }, ref) => (
    <p
      ref={ref}
      style={{ ...styles.description, ...style }}
      className={className}
      {...props}
    />
  )
)
CardDescription.displayName = 'CardDescription'

export const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, style, ...props }, ref) => (
    <div
      ref={ref}
      style={{ ...styles.content, ...style }}
      className={className}
      {...props}
    />
  )
)
CardContent.displayName = 'CardContent'

export const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, style, ...props }, ref) => (
    <div
      ref={ref}
      style={{ ...styles.footer, ...style }}
      className={className}
      {...props}
    />
  )
)
CardFooter.displayName = 'CardFooter'

const styles: Record<string, CSSProperties> = {
  card: {
    background: 'var(--bg-secondary)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-md)',
    boxShadow: 'var(--shadow-sm)',
    color: 'var(--text-primary)',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    padding: '24px',
  },
  title: {
    fontSize: '18px',
    fontWeight: 600,
    lineHeight: 1,
    letterSpacing: '-0.01em',
    margin: 0,
  },
  description: {
    fontSize: '14px',
    color: 'var(--text-secondary)',
    margin: 0,
  },
  content: {
    padding: '0 24px 24px 24px',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    padding: '0 24px 24px 24px',
  },
}
